/**
 * Geometry of the quiz band strip: where each band's range falls on 0 to 100.
 *
 * Whether bands overlap or leave a gap is a sign-off readiness rule, and `WebValidator`'s readiness
 * owns it — its message is what the Sign Off list shows. This file only draws where the (possibly
 * unsaved) ranges fall, so the editor can show an overlap as it is typed. It never blocks a save and
 * never words a problem.
 */

export interface QuizBandRange {
    Name?: string | null
    MinScore: number
    MaxScore: number
}

export type BandStripSegment =
    | { kind: 'band'; bandIndex: number; rank: number; label: string; from: number; to: number; widthPercent: number }
    | { kind: 'overlap'; bandIndices: number[]; from: number; to: number; widthPercent: number }
    | { kind: 'gap'; from: number; to: number; widthPercent: number }

export interface BandStripTick {
    value: number
    leftPercent: number
    align: 'start' | 'center' | 'end'
    /** The tick bounds an overlap. */
    isOverlap: boolean
}

export interface BandStripGeometry {
    segments: BandStripSegment[]
    ticks: BandStripTick[]
    /** Band fields whose value falls inside an overlap, keyed by the band's index in the input. */
    overlappingFields: Array<{ bandIndex: number; field: 'MinScore' | 'MaxScore' }>
}

const SCORE_MAX = 100
/** 0 to 100 inclusive is 101 whole scores; a range's width is its share of them. */
const SCORE_COUNT = SCORE_MAX + 1

/** A band that can be drawn: whole-number bounds inside 0 to 100, low to high. */
export function isDrawableBand(band: QuizBandRange): boolean {
    return Number.isInteger(band.MinScore) && Number.isInteger(band.MaxScore)
        && band.MinScore >= 0 && band.MaxScore <= SCORE_MAX && band.MinScore <= band.MaxScore
}

const percentOf = (score: number) => (score / SCORE_COUNT) * 100

/**
 * Lay the bands out along 0 to 100. Bands that can't be drawn are left off the strip; the rest are
 * ranked lowest first, which is the order the strip's shades follow.
 */
export function bandStripGeometry(bands: QuizBandRange[]): BandStripGeometry {
    const drawable = bands
        .map((band, bandIndex) => ({ band, bandIndex }))
        .filter(({ band }) => isDrawableBand(band))
        .sort((a, b) => a.band.MinScore - b.band.MinScore || a.band.MaxScore - b.band.MaxScore)
    const rankOf = new Map(drawable.map(({ bandIndex }, rank) => [bandIndex, rank]))

    const coverAt = (score: number) => drawable
        .filter(({ band }) => band.MinScore <= score && score <= band.MaxScore)
        .map(({ bandIndex }) => bandIndex)

    const segments: BandStripSegment[] = []
    let runStart = 0
    let runCover = coverAt(0)
    const closeRun = (to: number) => {
        const from = runStart
        const widthPercent = percentOf(to - from + 1)
        if (runCover.length === 0) segments.push({ kind: 'gap', from, to, widthPercent })
        else if (runCover.length > 1) segments.push({ kind: 'overlap', bandIndices: runCover, from, to, widthPercent })
        else {
            const bandIndex = runCover[0]
            segments.push({ kind: 'band', bandIndex, rank: rankOf.get(bandIndex) ?? 0, label: bands[bandIndex].Name ?? '', from, to, widthPercent })
        }
    }
    for (let score = 1; score <= SCORE_MAX; score++) {
        const cover = coverAt(score)
        if (cover.join(',') !== runCover.join(',')) {
            closeRun(score - 1)
            runStart = score
            runCover = cover
        }
    }
    closeRun(SCORE_MAX)

    const ticks: BandStripTick[] = [{ value: 0, leftPercent: 0, align: 'start', isOverlap: segments[0]?.kind === 'overlap' }]
    for (let i = 1; i < segments.length; i++) {
        const previous = segments[i - 1]
        const segment = segments[i]
        // After an overlap the tick names where the overlap ends, so the pair reads as its range.
        if (previous.kind === 'overlap') ticks.push({ value: previous.to, leftPercent: percentOf(previous.to), align: 'center', isOverlap: true })
        else ticks.push({ value: segment.from, leftPercent: percentOf(segment.from), align: 'center', isOverlap: segment.kind === 'overlap' })
    }
    const last = segments[segments.length - 1]
    const closing: BandStripTick = { value: SCORE_MAX, leftPercent: 100, align: 'end', isOverlap: last?.kind === 'overlap' }
    const uniqueTicks = [...ticks.filter(tick => tick.value !== SCORE_MAX), closing]

    const overlappingFields: BandStripGeometry['overlappingFields'] = []
    for (const segment of segments) {
        if (segment.kind !== 'overlap') continue
        for (const bandIndex of segment.bandIndices) {
            const band = bands[bandIndex]
            if (band.MinScore >= segment.from && band.MinScore <= segment.to) overlappingFields.push({ bandIndex, field: 'MinScore' })
            if (band.MaxScore >= segment.from && band.MaxScore <= segment.to) overlappingFields.push({ bandIndex, field: 'MaxScore' })
        }
    }

    return { segments, ticks: uniqueTicks, overlappingFields }
}
